import { useEffect } from "react";
import { Link, useLocation, Navigate } from "react-router-dom";
import { ChevronRight, ExternalLink } from "lucide-react";
import Seo from "@/components/Seo";
import { Reveal } from "@/components/Reveal";
import { LEGAL_DOCS, COMPANY } from "./legalContent";

export default function LegalPage() {
  const { pathname } = useLocation();
  const doc = pathname.replace(/^\//, "");
  const data = LEGAL_DOCS[doc];

  useEffect(() => window.scrollTo(0, 0), [doc]);

  if (!data) return <Navigate to="/" replace />;

  const url = typeof window !== "undefined" ? window.location.origin + window.location.pathname : "";
  const breadcrumbLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Acasă", item: window.location.origin + "/" },
      { "@type": "ListItem", position: 2, name: data.title, item: url },
    ],
  };

  return (
    <div className="pt-20" data-testid={`legal-${doc}`}>
      <Seo title={data.title} description={data.subtitle} jsonLd={breadcrumbLd} />

      <section className="bg-ink text-cream">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-20 md:py-28">
          <p className="overline text-sand">{data.subtitle}</p>
          <h1 className="font-display text-5xl md:text-8xl tracking-tight mt-3" data-testid="legal-title">{data.title}</h1>
          <p className="text-cream/50 text-sm mt-6">Ultima actualizare: {data.updated}</p>
        </div>
      </section>

      <div className="max-w-3xl mx-auto px-5 md:px-10 py-16 md:py-24">
        <nav className="flex items-center gap-2 text-xs text-muted-foreground mb-12">
          <Link to="/" className="hover:text-roast">Acasă</Link>
          <ChevronRight size={13} />
          <span className="text-ink">{data.title}</span>
        </nav>

        <div className="space-y-14">
          {data.sections.map((s, i) => (
            <Reveal key={i} delay={i * 0.04}>
              <section>
                <h2 className="font-display text-2xl md:text-3xl tracking-tight mb-5">{s.h}</h2>
                <div className="space-y-4 text-muted-foreground leading-relaxed">
                  {s.body.map((p, j) => (
                    <p key={j}>{p}</p>
                  ))}
                </div>
                {s.links && (
                  <ul className="mt-6 space-y-3">
                    {s.links.map((l) => (
                      <li key={l.href}>
                        <a
                          href={l.href}
                          target="_blank"
                          rel="noopener noreferrer nofollow"
                          data-testid={`legal-link-${l.href.replace(/[^a-z]/gi, "").slice(0, 20)}`}
                          className="inline-flex items-center gap-2 text-roast font-medium hover:underline"
                        >
                          {l.label}
                          <ExternalLink size={14} />
                        </a>
                      </li>
                    ))}
                  </ul>
                )}
              </section>
            </Reveal>
          ))}
        </div>

        <div className="mt-20 pt-10 border-t border-line text-sm text-muted-foreground">
          <p className="font-medium text-ink mb-2">{COMPANY.name}</p>
          <p>{COMPANY.address}</p>
          <p>CUI: {COMPANY.cui} · Reg. Com.: {COMPANY.reg}</p>
          <p className="mt-2">{COMPANY.email} · {COMPANY.phone}</p>
        </div>
      </div>
    </div>
  );
}
